import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
  Tooltip,
} from '@mui/joy';
import { CONSUMER_CONTRACT_REGISTRY_MANIFEST } from '../registry/consumerContractRegistry.manifest';
import type {
  AudienceKind,
  ChangePropagation,
  ConsumerContractArtifact,
  FlatSubscriptionRow,
  NewValuePropagationKind,
  OperationEffect,
  RegistryFilterState,
  RiskClassification,
  SourceCategory,
  SubscriptionKind,
  UsageKind,
  ValueHandlingKind,
  ValuesSourceKind,
} from '../registry/consumerContractRegistry.types';
import {
  buildEffectStatement,
  buildFieldIndex,
  countFieldIndexSubscribers,
  countSubscriptions,
  EMPTY_REGISTRY_FILTERS,
  filterFieldIndex,
  filterFlatSubscriptions,
  flattenManifestSubscriptions,
  labelForAccessMode,
  labelForAdditionalCopy,
  labelForAudience,
  labelForChangePropagation,
  labelForDomainOwner,
  labelForOperation,
  labelForRelationshipKind,
  labelForRenderSource,
  labelForRiskClassification,
  labelForRoute,
  labelForSafeguard,
  labelForUsage,
  labelForValueHandling,
  labelForValuesSource,
  labelForNewValuePropagation,
  labelForWriteBack,
  routeTooltip,
} from '../registry/consumerContractRegistry.utils';
import {
  buildRegistryAttentionDigest,
  formatRegistryAttentionDigestMarkdown,
  registryAttentionDigestFilename,
  subscriptionNeedsAttention,
} from '../registry/consumerContractRegistry.digest';
import { validateConsumerContractManifest } from '../registry/consumerContractRegistry.validate';
import {
  buildTrickleVentImpactMatrix,
  countFieldNewValueImpact,
  countValueHandlingClassifications,
  listNonImageValueHandlingRows,
} from '../registry/consumerContractRegistry.valueHandling';
import {
  SEED_DEFINITIONS,
  complianceColor,
  getSeedDefinition,
  labelForSeedCompliance,
} from '../registry/consumerContractRegistry.seedDefinitions';
import type { SeedDefinition } from '../registry/consumerContractRegistry.types';

const RISK_COLORS: Record<RiskClassification, 'success' | 'warning' | 'danger' | 'neutral'> = {
  expected_neutral: 'success',
  attention: 'warning',
  drift_risk: 'danger',
  conflicting_authority: 'danger',
};

const PROPAGATION_COLORS: Record<ChangePropagation, 'success' | 'warning' | 'danger' | 'neutral'> = {
  automatic_shared: 'success',
  separate_update_required: 'warning',
  generated_from_shared_contract: 'neutral',
  no_propagation_required: 'neutral',
  unknown: 'warning',
};

const VALUE_HANDLING_COLORS: Record<ValueHandlingKind, 'success' | 'warning' | 'danger' | 'neutral'> = {
  value_agnostic: 'success',
  value_aware: 'warning',
  pass_through_with_constraints: 'neutral',
  unknown: 'neutral',
};

const NEW_VALUE_COLORS: Record<NewValuePropagationKind, 'success' | 'warning' | 'danger' | 'neutral'> = {
  automatic: 'success',
  verification_only: 'neutral',
  mapping_update_required: 'warning',
  validation_sanitisation_update_required: 'warning',
  supplier_contract_update_required: 'danger',
  unknown: 'neutral',
};

function ValueHandlingBadge({
  kind,
  rationale,
}: {
  kind: ValueHandlingKind;
  rationale: string;
}) {
  if (kind === 'unknown') return null;
  return (
    <Tooltip title={rationale} placement="top" variant="soft">
      <Chip size="sm" variant="soft" color={VALUE_HANDLING_COLORS[kind]} data-testid="ccr-value-handling-badge">
        {labelForValueHandling(kind)}
      </Chip>
    </Tooltip>
  );
}

function NewValuePropagationBadge({
  propagation,
  rationale,
}: {
  propagation: NewValuePropagationKind;
  rationale: string;
}) {
  return (
    <Tooltip title={rationale} placement="top" variant="soft">
      <Chip size="sm" variant="outlined" color={NEW_VALUE_COLORS[propagation]} data-testid="ccr-new-value-badge">
        {labelForNewValuePropagation(propagation)}
      </Chip>
    </Tooltip>
  );
}
function RenderSourceBadge({ value }: { value: string }) {
  return (
    <Chip size="sm" variant="soft" color="neutral">
      {labelForRenderSource(value)}
    </Chip>
  );
}

function RouteBadge({
  kind,
  viaBuilder,
  kinds,
}: {
  kind: SubscriptionKind;
  viaBuilder?: string;
  kinds?: SubscriptionKind[];
}) {
  const label =
    kinds && kinds.length > 1
      ? kinds.map((k) => labelForRoute(k, k === kind ? viaBuilder : undefined)).join(' + ')
      : labelForRoute(kind, viaBuilder);

  return (
    <Tooltip title={routeTooltip(kind, viaBuilder)} placement="top" variant="soft">
      <Chip size="sm" variant="outlined" color="neutral" data-testid="ccr-route-badge">
        {label}
      </Chip>
    </Tooltip>
  );
}

function OperationBadge({ operation }: { operation: OperationEffect }) {
  return (
    <Chip size="sm" variant="soft" color="success" data-testid="ccr-operation-badge">
      {labelForOperation(operation)}
    </Chip>
  );
}

function OperationRiskBadge({ risk, rationale }: { risk: RiskClassification; rationale: string }) {
  return (
    <Tooltip title={rationale} placement="top" variant="soft">
      <Chip size="sm" variant="soft" color={RISK_COLORS[risk]} data-testid="ccr-operation-risk-badge">
        {labelForRiskClassification(risk)}
      </Chip>
    </Tooltip>
  );
}

function PropagationBadge({
  propagation,
  rationale,
}: {
  propagation: ChangePropagation;
  rationale: string;
}) {
  return (
    <Tooltip title={rationale} placement="top" variant="soft">
      <Chip size="sm" variant="soft" color={PROPAGATION_COLORS[propagation]} data-testid="ccr-propagation-badge">
        {labelForChangePropagation(propagation)}
      </Chip>
    </Tooltip>
  );
}

function RegistryLegend() {
  return (
    <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm' }} data-testid="ccr-legend">
      <Typography level="title-sm" sx={{ mb: 1 }}>
        Badge legend
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, fontSize: 'sm' }}>
        <Box>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>
            Route & ownership
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
            Domain owner · Current read source · Route (Referenced, Via shared builder, Public PDF mapper) — neutral;
            route alone does not imply risk.
          </Typography>
        </Box>
        <Box>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>
            Operation (what the consumer does)
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
            Read and format · Calculated · Operation risk (Expected = safe read/format with no write-back or duplicate
            persistence).
          </Typography>
        </Box>
        <Box>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>
            Change propagation (independent)
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
            Automatic / shared · Generated from shared contract (Edge Function boundary) · No propagation required
            (stored PNG reads; legacy conditional paths) · Separate update required (amber) only where parallel
            mapper logic can still drift on active v2_lite paths.
          </Typography>
        </Box>
      </Box>
    </Sheet>
  );
}

function FilterBar({
  filters,
  onChange,
  artifacts,
}: {
  filters: RegistryFilterState;
  onChange: (next: RegistryFilterState) => void;
  artifacts: ConsumerContractArtifact[];
}) {
  const set = (patch: Partial<RegistryFilterState>) => onChange({ ...filters, ...patch });

  return (
    <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm' }}>
      <Typography level="title-sm" sx={{ mb: 1.5 }}>
        Filters
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr repeat(3, 1fr)', lg: '2fr repeat(7, 1fr)' },
          gap: 1.5,
          alignItems: 'end',
        }}
      >
        <FormControl sx={{ gridColumn: { xs: '1', lg: '1 / span 2' } }}>
          <FormLabel>Search</FormLabel>
          <Input
            placeholder="Search field, path, effect, code ref…"
            value={filters.search}
            onChange={(e) => set({ search: e.target.value })}
            data-testid="ccr-search"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Operation risk</FormLabel>
          <Select
            value={filters.riskClassification}
            onChange={(_, v) => set({ riskClassification: (v as RiskClassification) || '' })}
            data-testid="ccr-filter-operation-risk"
          >
            <Option value="">All</Option>
            {(
              ['expected_neutral', 'attention', 'drift_risk', 'conflicting_authority'] as RiskClassification[]
            ).map((r) => (
              <Option key={r} value={r}>
                {labelForRiskClassification(r)}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Propagation</FormLabel>
          <Select
            value={filters.changePropagation}
            onChange={(_, v) => set({ changePropagation: (v as ChangePropagation) || '' })}
            data-testid="ccr-filter-propagation"
          >
            <Option value="">All</Option>
            {(
              [
                'automatic_shared',
                'separate_update_required',
                'generated_from_shared_contract',
                'no_propagation_required',
                'unknown',
              ] as ChangePropagation[]
            ).map((p) => (
              <Option key={p} value={p}>
                {labelForChangePropagation(p)}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Operation</FormLabel>
          <Select
            value={filters.operation}
            onChange={(_, v) => set({ operation: (v as OperationEffect) || '' })}
            data-testid="ccr-filter-operation"
          >
            <Option value="">All</Option>
            {(
              [
                'read_only',
                'read_and_format',
                'calculate_derive',
                'create_cached_representation',
                'persist_duplicate',
                'mutate_source',
              ] as OperationEffect[]
            ).map((o) => (
              <Option key={o} value={o}>
                {labelForOperation(o)}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Artifact</FormLabel>
          <Select value={filters.artifactId} onChange={(_, v) => set({ artifactId: (v as string) || '' })}>
            <Option value="">All</Option>
            {artifacts.map((a) => (
              <Option key={a.id} value={a.id}>
                {a.name}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Audience</FormLabel>
          <Select value={filters.audience} onChange={(_, v) => set({ audience: (v as AudienceKind) || '' })}>
            <Option value="">All</Option>
            {(['staff', 'customer', 'supplier', 'internal'] as AudienceKind[]).map((a) => (
              <Option key={a} value={a}>
                {labelForAudience(a)}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Usage</FormLabel>
          <Select value={filters.usage} onChange={(_, v) => set({ usage: (v as UsageKind) || '' })}>
            <Option value="">All</Option>
            {(['displayed', 'exported', 'calculated', 'conditional', 'omitted'] as UsageKind[]).map((u) => (
              <Option key={u} value={u}>
                {labelForUsage(u)}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Value handling</FormLabel>
          <Select
            value={filters.valueHandling}
            onChange={(_, v) => set({ valueHandling: (v as ValueHandlingKind) || '' })}
            data-testid="ccr-filter-value-handling"
          >
            <Option value="">All</Option>
            {(['value_agnostic', 'value_aware', 'pass_through_with_constraints', 'unknown'] as ValueHandlingKind[]).map(
              (v) => (
                <Option key={v} value={v}>
                  {labelForValueHandling(v)}
                </Option>
              ),
            )}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>New value</FormLabel>
          <Select
            value={filters.newValuePropagation}
            onChange={(_, v) => set({ newValuePropagation: (v as NewValuePropagationKind) || '' })}
            data-testid="ccr-filter-new-value"
          >
            <Option value="">All</Option>
            {(
              [
                'automatic',
                'verification_only',
                'mapping_update_required',
                'validation_sanitisation_update_required',
                'supplier_contract_update_required',
                'unknown',
              ] as NewValuePropagationKind[]
            ).map((v) => (
              <Option key={v} value={v}>
                {labelForNewValuePropagation(v)}
              </Option>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Sheet>
  );
}

function TechnicalDetails({ row }: { row: FlatSubscriptionRow }) {
  return (
    <Box sx={{ fontSize: 'xs', color: 'neutral.600' }}>
      <div>
        <strong>Relationship:</strong> {labelForRelationshipKind(row.relationshipKind)}
      </div>
      <div>
        <strong>Write-back:</strong> {labelForWriteBack(row.writeBack)}
      </div>
      <div>
        <strong>Persisted copy:</strong> {labelForAdditionalCopy(row.additionalPersistedCopy)}
      </div>
      <div>
        <strong>Safeguard:</strong> {labelForSafeguard(row.safeguard)}
      </div>
      <div>
        <strong>Access:</strong> {labelForAccessMode(row.accessMode)}
      </div>
      <div>
        <strong>Operation rationale:</strong> {row.riskRationale}
      </div>
      <div>
        <strong>Propagation rationale:</strong> {row.propagationRationale}
      </div>
      <div>
        <strong>Seed:</strong> {row.seedKey ? <code>{row.seedKey}</code> : '—'}
      </div>
      <div>
        <strong>Value handling:</strong> {labelForValueHandling(row.valueHandling)} ·{' '}
        {labelForValuesSource(row.valuesSource)} · {labelForNewValuePropagation(row.newValuePropagation)}
      </div>
      <div>
        <strong>Value rationale:</strong> {row.valueHandlingRationale}
      </div>
      <div>
        <strong>Code:</strong> <code>{row.codeReference}</code>
      </div>
    </Box>
  );
}

function SubscriptionTable({ rows }: { rows: FlatSubscriptionRow[] }) {
  if (rows.length === 0) {
    return (
      <Sheet variant="soft" sx={{ p: 3, textAlign: 'center' }}>
        <Typography level="body-md" color="neutral">
          No subscriptions match the current filters.
        </Typography>
      </Sheet>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: 1200,
          borderCollapse: 'collapse',
          fontSize: 'sm',
          '& th, & td': {
            borderBottom: '1px solid',
            borderColor: 'divider',
            p: 1,
            verticalAlign: 'top',
            textAlign: 'left',
          },
          '& th': { fontWeight: 600, bgcolor: 'background.level1', whiteSpace: 'nowrap' },
          '& code': { fontSize: 'xs', wordBreak: 'break-all' },
        }}
        data-testid="ccr-subscription-table"
      >
        <thead>
          <tr>
            <th>Field</th>
            <th>Domain owner</th>
            <th>Current source</th>
            <th>Route</th>
            <th>Operation</th>
            <th>Op. risk</th>
            <th>Propagation</th>
            <th>Value handling</th>
            <th>New value</th>
            <th>Output</th>
            <th>Technical details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.subscribedField}</td>
              <td>
                <Chip size="sm" variant="soft" color="neutral">
                  {labelForDomainOwner(row.domainOwner)}
                </Chip>
              </td>
              <td>
                <code>{row.currentReadSource}</code>
              </td>
              <td>
                <RouteBadge kind={row.subscriptionKind} viaBuilder={row.viaBuilder} kinds={row.subscriptionKinds} />
              </td>
              <td>
                <OperationBadge operation={row.operation} />
              </td>
              <td>
                <OperationRiskBadge risk={row.riskClassification} rationale={row.riskRationale} />
              </td>
              <td>
                <PropagationBadge propagation={row.changePropagation} rationale={row.propagationRationale} />
              </td>
              <td>
                {row.excludedFromValueHandling ? (
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Image (n/a)</Typography>
                ) : (
                  <ValueHandlingBadge kind={row.valueHandling} rationale={row.valueHandlingRationale} />
                )}
              </td>
              <td>
                {row.excludedFromValueHandling ? (
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>—</Typography>
                ) : (
                  <NewValuePropagationBadge
                    propagation={row.newValuePropagation}
                    rationale={row.valueHandlingRationale}
                  />
                )}
              </td>
              <td>{row.outputLabelOrSlot}</td>
              <td>
                <TechnicalDetails row={row} />
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function warningColor(severity: string): 'warning' | 'danger' | 'neutral' {
  if (severity === 'propagation' || severity === 'duplicate_mapper') return 'warning';
  return 'danger';
}

function ArtifactCentricView({
  artifacts,
  filters,
}: {
  artifacts: ConsumerContractArtifact[];
  filters: RegistryFilterState;
}) {
  const flat = useMemo(() => flattenManifestSubscriptions(CONSUMER_CONTRACT_REGISTRY_MANIFEST), []);

  const visibleArtifacts = useMemo(() => {
    if (filters.artifactId) {
      return artifacts.filter((a) => a.id === filters.artifactId);
    }
    return artifacts;
  }, [artifacts, filters.artifactId]);

  if (visibleArtifacts.length === 0) {
    return (
      <Sheet variant="soft" sx={{ p: 3, textAlign: 'center' }}>
        <Typography level="body-md" color="neutral">
          No artifacts match the current filters.
        </Typography>
      </Sheet>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {visibleArtifacts.map((artifact) => {
        const rows = filterFlatSubscriptions(
          flat.filter((r) => r.artifactId === artifact.id),
          filters,
        );
        const builderRows = rows.filter((r) => r.subscriptionKind === 'aggregate');
        const fieldRows = rows.filter((r) => r.subscriptionKind !== 'aggregate');
        if (
          rows.length === 0 &&
          (filters.audience ||
            filters.sourceCategory ||
            filters.usage ||
            filters.search ||
            filters.operation ||
            filters.riskClassification ||
            filters.changePropagation)
        ) {
          return null;
        }

        const artifactWarnings = [
          ...(artifact.warnings || []),
          ...CONSUMER_CONTRACT_REGISTRY_MANIFEST.globalWarnings.filter((w) =>
            w.relatedSubscriptionIds?.some((id) => id.startsWith(`${artifact.id}.`)),
          ),
        ];

        return (
          <Sheet key={artifact.id} variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
              <Typography level="title-lg">{artifact.name}</Typography>
              <Chip size="sm" variant="outlined">
                {labelForAudience(artifact.audience)}
              </Chip>
              <RenderSourceBadge value={artifact.renderSource} />
              <Chip size="sm" variant="soft" color="neutral">
                {fieldRows.length} field subscription{fieldRows.length === 1 ? '' : 's'}
              </Chip>
            </Box>
            <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 1 }}>
              {artifact.description}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
              Entry: <code>{artifact.entryPoint}</code>
            </Typography>

            {artifactWarnings.map((w) => (
              <Alert
                key={w.id}
                color={warningColor(w.severity)}
                variant="soft"
                size="sm"
                sx={{ mb: 1 }}
                data-testid={`ccr-artifact-warning-${w.id}`}
              >
                {w.message}
              </Alert>
            ))}

            {builderRows.map((builder) => (
              <Alert key={builder.id} color="neutral" variant="soft" size="sm" sx={{ mb: 2 }}>
                <Typography level="body-xs">{buildEffectStatement(builder)}</Typography>
              </Alert>
            ))}

            <SubscriptionTable rows={fieldRows} />
          </Sheet>
        );
      })}
    </Box>
  );
}

function FieldIndexView({ filters }: { filters: RegistryFilterState }) {
  const entries = useMemo(
    () => filterFieldIndex(buildFieldIndex(CONSUMER_CONTRACT_REGISTRY_MANIFEST), filters),
    [filters],
  );

  if (entries.length === 0) {
    return (
      <Sheet variant="soft" sx={{ p: 3, textAlign: 'center' }} data-testid="ccr-field-index-empty">
        <Typography level="body-md" color="neutral">
          No fields match the current filters.
        </Typography>
      </Sheet>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="ccr-field-index">
      {entries.map((entry) => (
        <Sheet key={entry.fieldKey} variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
          <Typography level="title-md" sx={{ mb: 0.5 }}>
            {entry.subscribedField}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
            Field key: <code>{entry.fieldKey}</code>
            {entry.seedKey && (
              <>
                {' '}
                · Seed: <code>{entry.seedKey}</code>
                {(() => {
                  const seed = getSeedDefinition(entry.seedKey!);
                  if (!seed) return null;
                  return (
                    <>
                      {' '}
                      <Chip size="sm" variant="soft" color={complianceColor(seed.compliance_status)}>
                        {labelForSeedCompliance(seed.compliance_status)}
                      </Chip>
                    </>
                  );
                })()}
              </>
            )}
          </Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            {countFieldIndexSubscribers(entry)} subscriber{countFieldIndexSubscribers(entry) === 1 ? '' : 's'}
          </Typography>
          {(() => {
            const impact = countFieldNewValueImpact(entry.subscribers);
            const mapping = impact.mapping_update_required + impact.supplier_contract_update_required;
            const auto = impact.automatic + impact.verification_only;
            if (mapping === 0 && auto === 0 && impact.unknown === 0) return null;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {auto > 0 && (
                  <Chip size="sm" variant="soft" color="success">
                    {auto} auto/verify
                  </Chip>
                )}
                {mapping > 0 && (
                  <Chip size="sm" variant="soft" color="warning">
                    {mapping} mapping/supplier
                  </Chip>
                )}
                {impact.validation_sanitisation_update_required > 0 && (
                  <Chip size="sm" variant="soft" color="warning">
                    {impact.validation_sanitisation_update_required} sanitize
                  </Chip>
                )}
                {impact.unknown > 0 && (
                  <Chip size="sm" variant="soft" color="neutral">
                    {impact.unknown} unknown
                  </Chip>
                )}
              </Box>
            );
          })()}
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 'sm' }}>
            {entry.subscribers.map((subRow) => (
              <Box component="li" key={`${subRow.artifactId}-${subRow.fieldKey}-${subRow.subscriptionKind}`} sx={{ mb: 1 }}>
                <strong>{subRow.artifactName}</strong>{' '}
                <Chip size="sm" variant="soft" color="neutral">
                  {labelForDomainOwner(subRow.domainOwner)}
                </Chip>{' '}
                <RouteBadge kind={subRow.subscriptionKind} viaBuilder={subRow.viaBuilder} kinds={subRow.subscriptionKinds} />{' '}
                <OperationBadge operation={subRow.operation} />{' '}
                <OperationRiskBadge risk={subRow.riskClassification} rationale={subRow.riskRationale} />{' '}
                <PropagationBadge propagation={subRow.changePropagation} rationale={subRow.propagationRationale} />
                {!subRow.excludedFromValueHandling && (
                  <>
                    {' '}
                    <ValueHandlingBadge kind={subRow.valueHandling} rationale={subRow.valueHandlingRationale} />{' '}
                    <NewValuePropagationBadge
                      propagation={subRow.newValuePropagation}
                      rationale={subRow.valueHandlingRationale}
                    />
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Sheet>
      ))}
    </Box>
  );
}

function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function SeedContractPanel({ seed }: { seed: SeedDefinition }) {
  return (
    <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm' }} data-testid={`ccr-seed-${seed.seedKey}`}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <Typography level="title-sm">Seed contract: {seed.seedKey}</Typography>
        <Chip size="sm" variant="soft" color={complianceColor(seed.compliance_status)}>
          {labelForSeedCompliance(seed.compliance_status)}
        </Chip>
        <Chip size="sm" variant="outlined" color="neutral">
          observed vs target
        </Chip>
      </Box>
      <Typography level="body-sm" sx={{ mb: 1 }}>
        {seed.meaning}
      </Typography>
      <Typography level="body-xs" sx={{ color: 'neutral.600', mb: 1 }}>
        Semantic authority: <code>{seed.semanticAuthority.path}</code>
        {seed.semanticAuthority.companionPaths?.map((p) => (
          <span key={p}>
            {' '}
            · <code>{p}</code>
          </span>
        ))}
      </Typography>
      <Typography level="body-xs" sx={{ color: 'neutral.600', mb: 1 }}>
        Allowed (observed): {seed.allowedValues.observed.join(', ')} · Target:{' '}
        {seed.allowedValues.target.join(', ')}
      </Typography>
      {seed.projections.map((proj) => (
        <Typography key={proj.path} level="body-xs" sx={{ color: 'neutral.600', mb: 0.5 }}>
          Projection <code>{proj.path}</code> ({proj.role}, non-authoritative for display): forward{' '}
          {proj.forwardMapping.split(':')[0]}
          {proj.vocabularyNote ? ` — ${proj.vocabularyNote}` : ''}
        </Typography>
      ))}
      <Typography level="body-xs" sx={{ mt: 1, mb: 0.5 }}>
        <strong>Observed:</strong> {seed.observed_current.precedenceOnDisagreement}
      </Typography>
      <Typography level="body-xs" sx={{ mb: 1 }}>
        <strong>Target:</strong> {seed.target_contract.precedenceOnDisagreement}
      </Typography>
      <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
        {seed.complianceNotes}
      </Typography>
      {seed.pendingValues?.map((pending) => (
        <Box key={pending.value} sx={{ mt: 1 }}>
          <Typography level="body-xs" sx={{ fontWeight: 600 }}>
            Pending: <code>{pending.value}</code>
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: 'xs' }}>
            {pending.decisionSequence.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </Box>
        </Box>
      ))}
    </Sheet>
  );
}

function ValueHandlingView({ filters }: { filters: RegistryFilterState }) {
  const manifest = CONSUMER_CONTRACT_REGISTRY_MANIFEST;
  const counts = useMemo(() => countValueHandlingClassifications(manifest), [manifest]);
  const trickleMatrix = useMemo(() => buildTrickleVentImpactMatrix(manifest), [manifest]);
  const rows = useMemo(
    () => filterFlatSubscriptions(listNonImageValueHandlingRows(manifest), filters),
    [manifest, filters],
  );

  return (
    <Box data-testid="ccr-value-handling-tab">
      {SEED_DEFINITIONS.map((seed) => (
        <SeedContractPanel key={seed.seedKey} seed={seed} />
      ))}
      <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm' }} data-testid="ccr-value-handling-legend">
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Value handling legend
        </Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.600', mb: 1 }}>
          Value-agnostic: opaque read/format · Value-aware: enum/code branches · Pass-through: raw value within
          whitelist · New value: what adding a value like <code>extender</code> requires per consumer.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip size="sm" variant="soft" color="success">
            {counts.valueHandling.value_agnostic} value-agnostic
          </Chip>
          <Chip size="sm" variant="soft" color="warning">
            {counts.valueHandling.value_aware} value-aware
          </Chip>
          <Chip size="sm" variant="soft" color="neutral">
            {counts.valueHandling.pass_through_with_constraints} pass-through
          </Chip>
          <Chip size="sm" variant="soft" color="neutral">
            {counts.totalExcludedImage} image (excluded)
          </Chip>
          <Chip size="sm" variant="soft" color="success">
            {counts.newValuePropagation.automatic + counts.newValuePropagation.verification_only} auto/verify
          </Chip>
          <Chip size="sm" variant="soft" color="warning">
            {counts.newValuePropagation.mapping_update_required} mapping updates
          </Chip>
          <Chip size="sm" variant="soft" color="danger">
            {counts.newValuePropagation.supplier_contract_update_required} supplier updates
          </Chip>
          <Chip size="sm" variant="soft" color="neutral">
            {counts.newValuePropagation.unknown} unknown
          </Chip>
        </Box>
      </Sheet>

      <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm' }} data-testid="ccr-trickle-vent-matrix">
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Worked example: trickle vent location → <code>extender</code>
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              minWidth: 720,
              borderCollapse: 'collapse',
              fontSize: 'sm',
              '& th, & td': { borderBottom: '1px solid', borderColor: 'divider', p: 1, verticalAlign: 'top' },
              '& th': { fontWeight: 600, bgcolor: 'background.level1' },
            }}
          >
            <thead>
              <tr>
                <th>Consumer</th>
                <th>Value handling</th>
                <th>New value impact</th>
                <th>extender</th>
              </tr>
            </thead>
            <tbody>
              {trickleMatrix.map((row) => (
                <tr key={`${row.consumer}-${row.inRegistry}`}>
                  <td>
                    {row.consumer}
                    {!row.inRegistry && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        (code path, not registry sub)
                      </Typography>
                    )}
                  </td>
                  <td>{labelForValueHandling(row.valueHandling)}</td>
                  <td>{labelForNewValuePropagation(row.newValuePropagation)}</td>
                  <td>{row.headExtenderImpact}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Sheet>

      <Typography level="title-sm" sx={{ mb: 1 }}>
        Non-image subscriptions ({rows.length})
      </Typography>
      <SubscriptionTable rows={rows} />
    </Box>
  );
}

function AttentionDigestView() {
  const manifest = CONSUMER_CONTRACT_REGISTRY_MANIFEST;
  const digest = useMemo(() => buildRegistryAttentionDigest(manifest), [manifest]);
  const markdown = useMemo(() => formatRegistryAttentionDigestMarkdown(digest), [digest]);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 2000);
    } catch {
      setCopyStatus('failed');
      window.setTimeout(() => setCopyStatus('idle'), 3000);
    }
  };

  const handleDownload = () => {
    downloadTextFile(registryAttentionDigestFilename(manifest.manifestVersion), markdown);
  };

  return (
    <Box data-testid="ccr-attention-digest">
      <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography level="title-md">Attention digest</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="sm" variant="outlined" onClick={handleCopy} data-testid="ccr-copy-digest">
              {copyStatus === 'copied' ? 'Copied' : copyStatus === 'failed' ? 'Copy failed' : 'Copy markdown'}
            </Button>
            <Button size="sm" variant="solid" onClick={handleDownload} data-testid="ccr-download-digest">
              Download .md
            </Button>
          </Box>
        </Box>
        <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 1.5 }}>
          Summarises anything that is not green in the registry UI: operation risk other than Expected, propagation
          that can drift (amber Separate update required), plus global and artifact warnings. Stored PNG reads and
          accepted contract boundaries are neutral.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip size="sm" variant="soft" color="warning">
            {digest.summary.attentionSubscriptions} non-green subscriptions
          </Chip>
          <Chip size="sm" variant="soft" color="neutral">
            {digest.summary.globalWarnings} global warnings
          </Chip>
          <Chip size="sm" variant="soft" color="neutral">
            {digest.summary.artifactWarnings} artifact warnings
          </Chip>
          <Chip size="sm" variant="soft" color="neutral">
            {digest.summary.fieldHotspots} field hotspots
          </Chip>
        </Box>
      </Sheet>

      {digest.globalWarnings.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            Global warnings
          </Typography>
          {digest.globalWarnings.map((w) => (
            <Alert key={w.id} color={warningColor(w.severity)} variant="soft" size="sm" sx={{ mb: 1 }}>
              {w.message}
            </Alert>
          ))}
        </Box>
      )}

      {digest.fieldHotspots.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Typography level="title-sm">Field hotspots</Typography>
          {digest.fieldHotspots.map((field) => (
            <Sheet key={field.fieldKey} variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
              <Typography level="title-md" sx={{ mb: 0.5 }}>
                {field.subscribedField}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
                Field key: <code>{field.fieldKey}</code>
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 'sm' }}>
                {field.subscribers.map((sub) => (
                  <Box component="li" key={`${field.fieldKey}-${sub.artifactName}`} sx={{ mb: 0.5 }}>
                    <strong>{sub.artifactName}</strong> — {sub.reasons.join('; ')}
                  </Box>
                ))}
              </Box>
            </Sheet>
          ))}
        </Box>
      )}

      {digest.subscriptions.length > 0 && (
        <Box>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            All non-green subscriptions ({digest.subscriptions.length})
          </Typography>
          <SubscriptionTable rows={flattenManifestSubscriptions(manifest).filter(subscriptionNeedsAttention)} />
        </Box>
      )}

      {digest.subscriptions.length === 0 && digest.globalWarnings.length === 0 && digest.artifactWarnings.length === 0 && (
        <Sheet variant="soft" sx={{ p: 3, textAlign: 'center' }}>
          <Typography level="body-md" color="neutral">
            No non-green items found — all subscriptions are Expected and no amber propagation items remain.
          </Typography>
        </Sheet>
      )}
    </Box>
  );
}

export const ConsumerContractRegistryPanel: React.FC = () => {
  const manifest = CONSUMER_CONTRACT_REGISTRY_MANIFEST;
  const validation = useMemo(() => validateConsumerContractManifest(manifest), [manifest]);
  const [filters, setFilters] = useState<RegistryFilterState>(EMPTY_REGISTRY_FILTERS);
  const [activeTab, setActiveTab] = useState(0);
  const totalSubs = countSubscriptions(manifest);

  if (!validation.valid) {
    return (
      <Alert color="danger" data-testid="ccr-manifest-error">
        <Typography level="title-md">Registry manifest validation failed</Typography>
        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
          {validation.issues.map((issue) => (
            <li key={`${issue.path}-${issue.message}`}>
              <code>{issue.path}</code>: {issue.message}
            </li>
          ))}
        </Box>
      </Alert>
    );
  }

  return (
    <Box data-testid="consumer-contract-registry-panel" data-readonly="true">
      <Alert color="primary" variant="soft" sx={{ mb: 2 }}>
        <Typography level="title-sm">Declared contract — not live quote data</Typography>
        <Typography level="body-sm">{manifest.disclaimer}</Typography>
        <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.600' }}>
          Manifest {manifest.manifestVersion} · Schema {manifest.schemaVersion} · Last reviewed{' '}
          {manifest.lastReviewedAt} · {manifest.artifacts.length} artifacts · {totalSubs} subscriptions
        </Typography>
      </Alert>

      {manifest.globalWarnings.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography level="title-sm">Known governance warnings</Typography>
          {manifest.globalWarnings.map((w) => (
            <Alert
              key={w.id}
              color={warningColor(w.severity)}
              variant="soft"
              size="sm"
              data-testid={`ccr-global-warning-${w.id}`}
            >
              {w.message}
            </Alert>
          ))}
        </Box>
      )}

      <RegistryLegend />
      <FilterBar filters={filters} onChange={setFilters} artifacts={manifest.artifacts} />

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v as number)} sx={{ mb: 2 }}>
        <TabList>
          <Tab>Artifact-centric view</Tab>
          <Tab>Field index</Tab>
          <Tab>Value handling</Tab>
          <Tab>Attention digest</Tab>
        </TabList>
        <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
          <ArtifactCentricView artifacts={manifest.artifacts} filters={filters} />
        </TabPanel>
        <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
          <FieldIndexView filters={filters} />
        </TabPanel>
        <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
          <ValueHandlingView filters={filters} />
        </TabPanel>
        <TabPanel value={3} sx={{ p: 0, pt: 2 }}>
          <AttentionDigestView />
        </TabPanel>
      </Tabs>

      <Divider sx={{ my: 2 }} />
      <Typography level="body-xs" color="neutral">
        Read-only governance view. Operation risk and change propagation are independent dimensions. This panel does not
        read or write quote payloads at runtime.
      </Typography>
    </Box>
  );
};

export default ConsumerContractRegistryPanel;
